import { gpHome } from '../../app';
import { AiCreditsRefreshEvent } from '../../services/ai-credits-contract';
import {
    AiCreditsPurchaseServiceName,
    IAiCreditsPurchaseApi,
} from '../../services/ai-credits-purchase-contract';
import type * as ng from 'angular';
import aiCreditsPurchaseTemplate from './ai-credits-purchase.html';

/**
 * The one part of buying credits that outlives the dialog: the return from Stripe.
 *
 * Choosing a pack and starting a checkout happen in gp-buy-credits-dialog, opened from
 * the AI Credit Usage card. The purchase invoice archive moved to "Credit purchases"
 * in the subscriptions panel. Confirming the payment can live in neither: Stripe
 * redirects back to the account page, by which time the dialog is long gone, and the
 * subscriptions panel is hidden on the licence-manager path. So this stays mounted
 * above the cards, rendering nothing until a payment comes back to be confirmed.
 */
interface gpAiCreditsPurchaseScope extends ng.IScope {
    errorText: string | null;

    /** Verifying the payment after the success redirect from Stripe. */
    confirming: boolean;
    /** The payment verified and the credits are in the ledger. */
    purchaseConfirmed: boolean;
    /** The payment could not be verified (yet) — the hourly backstop will post it. */
    confirmFailed: boolean;
}

gpHome.directive('gpAiCreditsPurchase', [
    '$location',
    '$rootScope',
    AiCreditsPurchaseServiceName,
    function (
        $location: ng.ILocationService,
        $rootScope: ng.IRootScopeService,
        gpAiCreditsPurchaseService: IAiCreditsPurchaseApi,
    ) {
        function link($scope: gpAiCreditsPurchaseScope) {
            $scope.errorText = null;
            $scope.confirming = false;
            $scope.purchaseConfirmed = false;
            $scope.confirmFailed = false;

            /**
             * Landing back from Stripe: the success URL carries
             * ?credits=success&session_id=... in the hash fragment. Verify with the
             * backend (which re-fetches the session from Stripe and posts the
             * credits idempotently), then refresh the balance panel. A cancelled
             * checkout is cleaned up quietly.
             */
            function handleReturnFromStripe() {
                const search = $location.search();
                if (search.credits === 'success' && search.session_id) {
                    const sessionId = String(search.session_id);
                    clearReturnParams();
                    $scope.confirming = true;
                    gpAiCreditsPurchaseService.confirmCheckout(sessionId)
                        .then(result => {
                            if (result.credited) {
                                $scope.purchaseConfirmed = true;
                                $rootScope.$broadcast(AiCreditsRefreshEvent);
                            } else {
                                $scope.confirmFailed = true;
                            }
                        })
                        .catch(() => $scope.confirmFailed = true)
                        .finally(() => $scope.confirming = false);
                } else if (search.credits === 'cancelled') {
                    clearReturnParams();
                }
            }

            function clearReturnParams() {
                // replace() so back doesn't re-trigger the confirm.
                $location.search('credits', null).search('session_id', null).replace();
            }

            handleReturnFromStripe();
        }

        return ({
            restrict: 'E',
            scope: {},
            link: (scope: gpAiCreditsPurchaseScope) => {
                link(scope);
            },
            template: aiCreditsPurchaseTemplate,
        });
    }
]);
